'use client';

import { useState, useEffect } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { Stack, Tooltip, IconButton } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ListAltIcon from '@mui/icons-material/ListAlt';

import { useRoleList, deleteRole, buildRoleSearchPayload } from '../../../stores/roleStore';

import MasterRoleAddEdit from './MasterRoleAddEdit';
import ManagementRoleDialog from './ManagementRoleDialog';
import { useLanguage } from '../../../utils/i18n';

// TAMBAHKAN IMPORT AXIOS CLIENT UNTUK HIT LOKAL
import { fleetApi } from '../../../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterRolePageContent() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [managementOpen, setManagementOpen] = useState(false);
  const [managementInitialTab, setManagementInitialTab] = useState(0);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [selectedRoleParentName, setSelectedRoleParentName] = useState('');

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const isRoleAdmin = currentUserRole === 'Administrator';

  const [searchField, setSearchField] = useState('all');
  const searchFieldOptions = [
    { value: 'all', label: t('general.searchAll', 'All') },
    { value: 'role_name', label: t('masterRole.roleName', 'Role Name') },
    { value: 'package_name', label: t('masterRole.packageName', 'Package Name') }
  ];

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const { data: rows, totalRecord, isLoading } = useRoleList(paginationModel.page, paginationModel.pageSize, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecord > 0 ? totalRecord : 9999;
      const payload = buildRoleSearchPayload(0, length, searchField, searchValue);

      const resp = await fleetApi.post('/roles/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      const fetchedRows = Array.isArray(data) ? data : (data?.data?.data ?? data?.data ?? []);
      return fetchedRows;
    } catch (err) {
      console.error('Role export all error', err);
      return [];
    }
  };

  // --- HANDLERS ---
  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenAddEditDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenAddEditDialog(true);
  };

  const handleAddEditClose = () => {
    setOpenAddEditDialog(false);
    setEditingId(null);
  };

  const handleRoleNameClick = (roleParent, roleName, tabIndex = 0) => {
    setSelectedRoleParentName(roleParent ?? '');
    setSelectedRoleName(roleName);
    setManagementInitialTab(tabIndex);
    setManagementOpen(true);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteRole(confirmTargetId);
      notify({ message: t('masterRole.deleteRoleSuccess', 'Role successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('masterRole.deleteRoleFailed', 'Failed to delete role'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleCellClick = (params, event) => {
    const getRoleName = localStorage.getItem('rolename');
    if (params.field === 'role_name') {
      handleRoleNameClick(getRoleName, params.row?.role_name);
    }
  };

  const columns = [
    {
      field: 'role_name',
      headerName: t('masterRole.roleName', 'Role Name'),
      flex: 2,
      renderCell: (params) => <span style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{params.value}</span>
    },
    ...(isRoleAdmin
      ? [
          {
            field: 'parent_name',
            headerName: t('masterRole.parentName', 'Parent Name'),
            flex: 2,
            renderCell: (params) =>
              params.row?.parent_name ?? params.row?.role_parent ?? params.row?.join?.parent_name ?? params.row?.join?.role_parent ?? '-'
          }
        ]
      : []),
    {
      field: 'package_name',
      headerName: t('masterRole.packageName', 'Package Name'),
      flex: 2,
      renderCell: (params) => params.row?.package_name ?? params.row?.join?.package_name ?? '-'
    },
    {
      field: 'manage_role',
      headerName: t('masterRole.manageRole', 'Manage Role'),
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const getRoleName = localStorage.getItem('rolename') || localStorage.getItem('role_name');
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Tooltip title={t('masterRole.manageMenu', 'Manage Menu')}>
              <IconButton size="small" color="primary" onClick={() => handleRoleNameClick(getRoleName, params.row?.role_name, 0)}>
                <ListAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('masterRole.manageCompany', 'Manage Company')}>
              <IconButton size="small" color="primary" onClick={() => handleRoleNameClick(getRoleName, params.row?.role_name, 1)}>
                <BusinessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('masterRole.manageVehicle', 'Manage Vehicle')}>
              <IconButton size="small" color="primary" onClick={() => handleRoleNameClick(getRoleName, params.row?.role_name, 2)}>
                <DirectionsCarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      }
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => {
        const rowId = params.row?.id ?? params.row?.role_id ?? params.id;
        if (isRoleAdmin) {
          return (
            <ActionMenu
              row={params.row}
              onEdit={() => handleEditClick(rowId)}
              onDelete={() => handleDelete(rowId, params.row.role_name)}
              mode="inline"
              disableEdit={params.row?.role_name === 'Administrator'}
              disableDelete={params.row?.role_name === 'Administrator'}
            />
          );
        } else {
          return (
            <ActionMenu
              row={params.row}
              onDelete={() => handleDelete(rowId, params.row.role_name)}
              mode="inline"
              disableDelete={params.row?.role_name === 'Administrator'}
              extraActions={[
                {
                  label: t('general.view', 'View'),
                  icon: <RemoveRedEyeIcon color="secondary" />,
                  onClick: () => handleEditClick(rowId)
                }
              ]}
            />
          );
        }
      }
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterRole.title', 'Master Role')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        onCellClick={handleCellClick}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecord}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchLabel={t('general.search', 'Search...')}
        onAddClick={handleAddClick}
        hideCompanyFilter={true}
        cursorPointer={true}
      />

      {openAddEditDialog && (
        <MasterRoleAddEdit
          open={openAddEditDialog}
          id={editingId}
          onClose={handleAddEditClose}
          onSuccess={() => setPaginationModel((p) => ({ ...p }))}
        />
      )}

      {managementOpen && (
        <ManagementRoleDialog
          open={managementOpen}
          roleName={selectedRoleName}
          roleParentName={selectedRoleParentName}
          initialTab={managementInitialTab}
          onClose={() => {
            setManagementOpen(false);
            setSelectedRoleName('');
            setSelectedRoleParentName('');
          }}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={() => setConfirmOpen(false)}
        itemName={confirmName}
        onConfirm={handleConfirmDelete}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={t('general.confirmDeleteMessage', 'Are you sure you want to delete this item?')}
        confirmLabel={t('general.delete', 'Delete')}
        cancelLabel={t('general.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function MasterRolePage() {
  return <MasterRolePageContent />;
}
